import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import api from '../../utils/api';
import { PlusCircle, Hash, Lightbulb, Loader2, Upload, Image as ImageIcon, Copy, Wallet as WalletIcon, Landmark, Coins, MoreHorizontal, Package } from 'lucide-react';

const WALLET_TYPE_META = {
  upi: { label: 'UPI', icon: WalletIcon, color: '#3b82f6' },
  bank: { label: 'Bank Transfer', icon: Landmark, color: '#22c55e' },
  usdt: { label: 'USDT / Crypto', icon: Coins, color: 'var(--primary)' },
  other: { label: 'Other', icon: MoreHorizontal, color: '#a78bfa' },
};

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const copyToClipboard = (text, label) => {
  if (!text) return;
  navigator.clipboard.writeText(text)
    .then(() => toast.success(`${label} copied`))
    .catch(() => toast.error('Failed to copy'));
};

// Deposits are how a user's wallet actually gets money in it — this is the
// step BEFORE a package purchase. User pays externally into one of admin's
// wallets, submits proof here, and once an admin approves it the amount
// lands in depositWallet, ready to spend on a package (see MyPackage.js).
export default function Deposit() {
  const { refreshUser } = useAuth();
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ amount: '', paymentMethod: 'INR', utrNumber: '' });
  const [wallets, setWallets] = useState([]);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [screenshot, setScreenshot] = useState('');
  const formRef = useRef(null);

  useEffect(() => {
    api.get('/deposits/my').then(r => setDeposits(r.data)).catch(console.error).finally(() => setLoading(false));
    api.get('/wallet/active').then(r => {
      setWallets(r.data);
      if (r.data.length > 0) {
        setSelectedWallet(r.data[0]);
        setForm(f => ({ ...f, paymentMethod: r.data[0].type === 'usdt' ? 'USDT' : 'INR' }));
      }
    }).catch(console.error);
  }, []);

  const handlePickWallet = (wallet) => {
    setSelectedWallet(wallet);
    setForm(f => ({ ...f, paymentMethod: wallet.type === 'usdt' ? 'USDT' : 'INR' }));
  };

  const handleScreenshotFile = async (file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) return toast.error('Screenshot must be under 4MB');
    const base64 = await fileToBase64(file);
    setScreenshot(base64);
  };

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amount = Number(form.amount);
    if (!amount || amount <= 0) return toast.error('Please enter a valid amount');
    if (wallets.length > 0 && !selectedWallet) return toast.error('Please select a payment wallet');
    if (!form.utrNumber.trim()) return toast.error('Please enter the UTR / transaction number');
    if (!screenshot) return toast.error('Please upload a screenshot of your payment');

    setSubmitting(true);
    try {
      const res = await api.post('/deposits/request', {
        ...form,
        amount,
        paymentWallet: selectedWallet?._id || null,
        paymentProof: screenshot
      });
      toast.success(res.data.message);
      setDeposits(prev => [res.data.deposit, ...prev]);
      setForm({ amount: '', paymentMethod: selectedWallet?.type === 'usdt' ? 'USDT' : 'INR', utrNumber: '' });
      setScreenshot('');
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (n) => `₹${(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <DashboardLayout>
      <h1 className="page-title">Add Funds</h1>
      <p className="page-subtitle">Add money to your wallet, then use it to purchase a package</p>

      {/* Your balance lives on the Wallet page (single source of truth) —
          just a quick link here instead of duplicating it as its own box. */}
      <div className="alert alert-info" style={{ marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <WalletIcon size={16} /> Approved deposits are credited to your Deposit Wallet — check your balance any time on the Wallet page.
        </span>
        <div style={{ display: 'flex', gap: 8 }}>
          <Link to="/dashboard/wallet" className="btn btn-outline btn-sm">View Wallet</Link>
          <Link to="/dashboard/package" className="btn btn-outline btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Package size={14} /> View Packages
          </Link>
        </div>
      </div>

      <div className="grid-2" style={{ gap: 24, marginBottom: 28 }}>
        {/* Deposit Request Form */}
        <div className="card" ref={formRef}>
          <div className="card-header">
            <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><PlusCircle size={18} /> Request Deposit</h3>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Amount (INR) *</label>
              <div className="input-group">
                <span className="input-icon">₹</span>
                <input
                  type="number"
                  name="amount"
                  className="form-control"
                  placeholder="Enter amount you paid"
                  value={form.amount}
                  onChange={handleChange}
                  min={1}
                  required
                />
              </div>
            </div>

            {/* Pay-to Wallet Selection */}
            {wallets.length > 0 && (
              <div className="form-group">
                <label>Pay To *</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: selectedWallet ? 12 : 0 }}>
                  {wallets.map(w => {
                    const meta = WALLET_TYPE_META[w.type] || WALLET_TYPE_META.other;
                    const active = selectedWallet?._id === w._id;
                    return (
                      <button
                        type="button"
                        key={w._id}
                        onClick={() => handlePickWallet(w)}
                        className={`btn ${active ? 'btn-primary' : 'btn-outline'} btn-sm`}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                      >
                        <meta.icon size={14} /> {w.label}
                      </button>
                    );
                  })}
                </div>

                {selectedWallet && (
                  <div className="card" style={{ background: 'rgba(var(--primary-rgb),0.06)', borderColor: 'rgba(var(--primary-rgb),0.25)', marginBottom: 0 }}>
                    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      {selectedWallet.qrImageUrl && (
                        <img src={selectedWallet.qrImageUrl} alt="QR" style={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 8, border: '1px solid rgba(var(--primary-rgb),0.3)' }} />
                      )}
                      <div style={{ flex: 1, minWidth: 180, fontSize: '0.85rem', color: 'var(--text-light)' }}>
                        {selectedWallet.type === 'upi' && selectedWallet.upiId && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            UPI ID: <strong>{selectedWallet.upiId}</strong>
                            <button type="button" className="btn btn-outline btn-sm" style={{ padding: '2px 6px' }} onClick={() => copyToClipboard(selectedWallet.upiId, 'UPI ID')}><Copy size={12} /></button>
                          </div>
                        )}
                        {selectedWallet.type === 'bank' && (
                          <>
                            {selectedWallet.accountHolderName && <div>Name: <strong>{selectedWallet.accountHolderName}</strong></div>}
                            {selectedWallet.bankName && <div>Bank: <strong>{selectedWallet.bankName}</strong></div>}
                            {selectedWallet.accountNumber && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                A/C No: <strong>{selectedWallet.accountNumber}</strong>
                                <button type="button" className="btn btn-outline btn-sm" style={{ padding: '2px 6px' }} onClick={() => copyToClipboard(selectedWallet.accountNumber, 'Account number')}><Copy size={12} /></button>
                              </div>
                            )}
                            {selectedWallet.ifscCode && <div>IFSC: <strong>{selectedWallet.ifscCode}</strong></div>}
                          </>
                        )}
                        {selectedWallet.type === 'usdt' && (
                          <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, wordBreak: 'break-all' }}>
                              <strong>{selectedWallet.usdtAddress}</strong>
                              <button type="button" className="btn btn-outline btn-sm" style={{ padding: '2px 6px', flexShrink: 0 }} onClick={() => copyToClipboard(selectedWallet.usdtAddress, 'Wallet address')}><Copy size={12} /></button>
                            </div>
                            {selectedWallet.usdtNetwork && <div style={{ marginTop: 4 }}>Network: <strong>{selectedWallet.usdtNetwork}</strong></div>}
                          </>
                        )}
                        {selectedWallet.type === 'other' && <div>{selectedWallet.otherDetails}</div>}
                        {selectedWallet.instructions && (
                          <div style={{ color: '#f59e0b', fontSize: '0.78rem', marginTop: 6 }}>{selectedWallet.instructions}</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="form-group">
              <label>Payment Method *</label>
              <select name="paymentMethod" className="form-control" value={form.paymentMethod} onChange={handleChange}>
                <option value="INR">INR (Bank Transfer / UPI)</option>
                <option value="USDT">USDT (Crypto)</option>
              </select>
            </div>
            <div className="form-group">
              <label>UTR / Transaction Number *</label>
              <div className="input-group">
                <span className="input-icon"><Hash size={16} /></span>
                <input
                  type="text"
                  name="utrNumber"
                  className="form-control"
                  placeholder="Enter UTR / TXN ID"
                  value={form.utrNumber}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label>Payment Screenshot *</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {screenshot && (
                  <img src={screenshot} alt="Payment screenshot preview" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, border: '1px solid rgba(var(--primary-rgb),0.2)' }} />
                )}
                <label className="btn btn-outline btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', margin: 0 }}>
                  {screenshot ? <><ImageIcon size={14} /> Change Screenshot</> : <><Upload size={14} /> Upload Screenshot</>}
                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleScreenshotFile(e.target.files[0])} />
                </label>
              </div>
              <small style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>Upload a screenshot of the payment you made to the wallet above.</small>
            </div>
            <div className="alert alert-info" style={{ fontSize: '0.82rem', marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <Lightbulb size={16} style={{ flexShrink: 0, marginTop: 2 }} />
              <span>After payment, submit this request. Once admin approves it, the amount is added to your wallet and you can purchase any package it covers.</span>
            </div>
            <button type="submit" className="btn btn-primary btn-block" disabled={submitting}>
              {submitting ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Loader2 size={16} className="spin" /> Submitting...</span>
              ) : (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><PlusCircle size={16} /> Submit Deposit Request</span>
              )}
            </button>
          </form>
        </div>

        {/* Info */}
        <div className="card" style={{ alignSelf: 'flex-start' }}>
          <div className="card-header"><h3 className="card-title">📋 How Deposits Work</h3></div>
          <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 14 }}>
            {[
              { icon: '1️⃣', text: 'Pay the amount you want to add, to one of the wallets shown above.' },
              { icon: '2️⃣', text: 'Submit this form with the UTR/transaction ID and a payment screenshot.' },
              { icon: '3️⃣', text: 'Admin reviews and approves your request (usually within 24 hours).' },
              { icon: '4️⃣', text: 'Once approved, the amount is credited to your wallet — spendable only on a package.' },
              { icon: '⚠️', text: 'This wallet balance cannot be withdrawn directly — it can only be used to purchase a package.' },
            ].map(r => (
              <li key={r.text} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <span style={{ background: 'rgba(var(--primary-rgb),0.1)', width: 30, height: 30, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '0.9rem' }}>{r.icon}</span>
                <span style={{ color: 'var(--text-light)', fontSize: '0.9rem', paddingTop: 5 }}>{r.text}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Deposit History */}
      <div className="card">
        <div className="card-header"><h3 className="card-title">📜 Deposit History</h3></div>
        {loading ? <div className="loader-wrap"><div className="loader"></div></div> :
          deposits.length === 0 ? (
            <div className="empty-state"><div className="empty-icon">💰</div><h3>No deposit requests yet</h3></div>
          ) : (
            <div className="table-wrap">
              <table className="tp-table">
                <thead>
                  <tr><th>Date</th><th>Amount</th><th>Method</th><th>UTR</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {deposits.map(d => (
                    <tr key={d._id}>
                      <td style={{ fontSize: '0.85rem' }}>{new Date(d.createdAt).toLocaleDateString('en-IN')}</td>
                      <td style={{ color: 'var(--primary)', fontWeight: 700 }}>{formatCurrency(d.amount)}</td>
                      <td>{d.paymentMethod}</td>
                      <td style={{ fontSize: '0.85rem' }}>{d.utrNumber || '—'}</td>
                      <td>
                        <span className={`badge badge-${d.status === 'approved' ? 'success' : d.status === 'rejected' ? 'danger' : 'warning'}`}>
                          {d.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </div>
    </DashboardLayout>
  );
}